import { useDashboardData } from "../../../context/DashboardContext";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { branding } from "../../../config/branding";
import { useActiveTheme } from "../../../context/ThemeContext";
import type { WidgetComponentProps } from "../types";

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);

const formatPercent = (value: number) => `${value.toFixed(1)}%`;

export default function AreaChartWidget({ config, title }: WidgetComponentProps) {
  const themeColors = useActiveTheme().colors;
  const { chartData } = useDashboardData();

  const dataKey = config.dataKey as string;
  const valueKey = (config.valueKey as string) || "value";
  const xKey = (config.xKey as string) || "date";
  const strokeColor = (config.strokeColor as string) || themeColors.chart[0] || branding.colors.chart[0];
  const gradientOpacity = (config.gradientOpacity as number) || 0.6;
  const height = (config.height as number) || 350;
  const yDomain = config.yDomain as [number, number] | undefined;
  const yFormatter = (config.yFormat as string) === "percent" ? formatPercent :
    (config.yFormat as string) === "currency" ? (v: number) => {
      if (v >= 1000000) return `$${(v / 1000000).toFixed(0)}M`;
      if (v >= 1000) return `$${(v / 1000).toFixed(0)}K`;
      return `$${v}`;
    } : undefined;
  const tooltipFormat = (config.tooltipFormat as string) === "percent" ? formatPercent : formatCurrency;
  const gradientId = `area-gradient-${dataKey}`;

  const data = (chartData[dataKey] as Record<string, unknown>[] | undefined) || [];

  if (data.length === 0) {
    return (
      <div className="bg-card rounded-xl border border-border p-6 h-full flex flex-col">
        <h3 className="text-lg font-semibold mb-4">{title}</h3>
        <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
          No data available
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border border-border p-6 h-full flex flex-col overflow-hidden">
      <h3 className="text-lg font-semibold mb-4">{title}</h3>
      <div className="flex-1" style={{ minHeight: height }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={strokeColor} stopOpacity={gradientOpacity} />
                <stop offset="100%" stopColor={strokeColor} stopOpacity={0.05} />
              </linearGradient>
            </defs>
            {config.showGrid && (
              <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-border" />
            )}
            <XAxis
              dataKey={xKey}
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11 }} className="text-muted-foreground"
              tickFormatter={(value: string) => {
                const date = new Date(value);
                return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
              }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11 }} className="text-muted-foreground"
              domain={yDomain}
              tickFormatter={yFormatter}
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
                      <p className="text-xs text-muted-foreground mb-1">{String(label)}</p>
                      <p className="text-sm font-semibold text-foreground">
                        {tooltipFormat(payload[0]?.value as number)}
                      </p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Area
              type="monotone"
              dataKey={valueKey}
              stroke={strokeColor}
              strokeWidth={2}
              fill={`url(#${gradientId})`}
              dot={false}
              activeDot={{ r: 5, fill: strokeColor, stroke: "white", strokeWidth: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
